import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useLead, Lead, useLeadStatusesForSelect } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { options: leadStatusOptions } = useLeadStatusesForSelect();
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'leadAssignedTo') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Assigned To',
          placeholder: 'Select user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'leadStatusId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Status',
          placeholder: 'Select status',
          options: leadStatusOptions,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormLead = Omit<Lead, 'clientId' | 'leadAssignedTo' | 'leadStatusId'> & {
      clientId?: string | number;
      leadAssignedTo?: string | number;
      leadStatusId?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: Lead): Partial<FormLead> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        leadAssignedTo: baseData.leadAssignedTo ? String(baseData.leadAssignedTo) : '',
        leadStatusId: baseData.leadStatusId ? String(baseData.leadStatusId) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormLead> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        leadAssignedTo: '',
        leadStatusId: '',
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormLead>) => {
      const processedData: Partial<Lead> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        leadAssignedTo: convertToNumber(data.leadAssignedTo) as number, // Required field
        leadStatusId: convertToNumber(data.leadStatusId) as number, // Required field
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormLead>) => {
      const processedData: Partial<Lead> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        leadAssignedTo: convertToNumber(data.leadAssignedTo) as number, // Required field
        leadStatusId: convertToNumber(data.leadStatusId) as number, // Required field
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Lead>,
      mapItemToFormData: customMapItemToFormData as (item: Lead) => Partial<Lead>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Lead>) => Promise<Lead>,
        update: customUpdate as (id: number, data: Partial<Lead>) => Promise<Lead>,
      },
    };
  })();

  return (
    <GenericFormPage<Lead>
      useData={useLead}
      config={configWithOptions}
    />
  );
};

export default Form;

